import { z } from 'zod';
import { FieldSchemaZod } from './field.schema.zod';
import { JsonValueZod } from './json.value.zod';
import { ValidatorRefZod } from './validator.ref.zod';

export const FieldGroupFieldSchemaZod = FieldSchemaZod.extend({
  searchable: z.boolean().optional(),
  /** When false, the field stays on the closed card and is omitted from the expand dialog. */
  dialog: z.boolean().optional(),
}).strict();

export const FieldGroupEmptyStateSchemaZod = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('search'), placeholder: z.string().min(1), resolver: z.string().min(1).optional() }).strict(),
  z.object({ kind: z.literal('placeholder'), label: z.string().min(1), icon: z.string().min(1).optional() }).strict(),
  // Display-only group: shows the label in every mode and never opens a picker. For a party a document
  // variant does not own (a quote template's customer), kept so the document keeps its shape.
  z.object({ kind: z.literal('note'), label: z.string().min(1) }).strict(),
]);

export const FieldGroupCardValueSchemaZod = z.object({ bind: z.string().min(1), path: z.string().min(1).optional(), empty: z.enum(['hide', 'not-set']).optional() }).strict();

export const FieldGroupCardParameterSchemaZod = z.union([FieldGroupCardValueSchemaZod, JsonValueZod]);

export const FieldGroupCardEntitySchemaZod = FieldGroupCardValueSchemaZod.extend({
  /** JSON-safe card inputs. A field-group card value resolves a live model bind. */
  params: z.record(z.string(), FieldGroupCardParameterSchemaZod).optional(),
});

export const FieldGroupCardSchemaZod = z.object({
  entity: FieldGroupCardEntitySchemaZod.optional(),
  title: FieldGroupCardValueSchemaZod.optional(),
  /** Muted qualifier under the title — who the titled party is, not a detail line (e.g. "t/a Beta"). */
  subtitle: FieldGroupCardValueSchemaZod.optional(),
  line1: FieldGroupCardValueSchemaZod.optional(),
  line2: FieldGroupCardValueSchemaZod.optional(),
  line3: FieldGroupCardValueSchemaZod.optional(),
  line4: FieldGroupCardValueSchemaZod.optional(),
  renderer: z.string().min(1).optional(),
}).strict().superRefine((card, context) => {
  if (card.entity || card.title) return;
  context.addIssue({ code: z.ZodIssueCode.custom, message: 'Field-group cards require an entity or title.', path: ['title'] });
});

export const FieldGroupSchemaZod = z.object({
  id: z.string().min(1),
  title: z.string().optional(),
  dialogTitle: z.string().optional(),
  colspan: z.number().int().positive().optional(),
  fields: z.array(FieldGroupFieldSchemaZod).min(1),
  emptyState: FieldGroupEmptyStateSchemaZod,
  card: FieldGroupCardSchemaZod,
  validators: z.array(ValidatorRefZod).optional(),
  renderer: z.string().min(1).optional(),
  adapter: z.string().min(1).optional(),
  draftResolver: z.string().min(1).optional(),
}).strict();

export type FieldGroupFieldSchema = z.infer<typeof FieldGroupFieldSchemaZod>;
export type FieldGroupEmptyStateSchema = z.infer<typeof FieldGroupEmptyStateSchemaZod>;
export type FieldGroupCardValueSchema = z.infer<typeof FieldGroupCardValueSchemaZod>;
export type FieldGroupCardParameterSchema = z.infer<typeof FieldGroupCardParameterSchemaZod>;
export type FieldGroupCardEntitySchema = z.infer<typeof FieldGroupCardEntitySchemaZod>;
export type FieldGroupCardSchema = z.infer<typeof FieldGroupCardSchemaZod>;
export type FieldGroupSchema = z.infer<typeof FieldGroupSchemaZod>;
